/**
 * Shared validation utilities to eliminate code duplication
 * across API routes.
 *
 * This module provides centralized validation functions for:
 * - Email addresses
 * - WAX blockchain account names
 * - Required field validation
 * - URL slugs (organization, campaign)
 * - Hex colors
 * - URLs
 * - Passwords
 */

// =============================================================================
// Email Validation
// =============================================================================

/**
 * Regular expression for validating email addresses.
 * Matches the pattern used throughout the codebase.
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates an email address format.
 *
 * @param email - The email address to validate
 * @returns true if the email format is valid
 *
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('invalid-email')     // false
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }
  return EMAIL_REGEX.test(email);
}

// =============================================================================
// WAX Account Validation
// =============================================================================

/**
 * Strict WAX account regex - exactly 12 characters, lowercase a-z and digits 1-5.
 * This is the standard WAX account name format for non-premium accounts.
 *
 * Used in: src/app/api/claims/route.ts
 */
export const WAX_ACCOUNT_STRICT_REGEX = /^[a-z1-5]{12}$/;

/**
 * Flexible WAX account regex - 1-12 characters with dots allowed.
 * Supports premium/bid names that can be shorter and contain periods.
 *
 * Used in: src/app/api/wax/check-account/route.ts
 */
export const WAX_ACCOUNT_FLEXIBLE_REGEX = /^[a-z1-5.]{1,12}$/;

/**
 * Legacy WAX account regex - allows 0-9 instead of just 1-5.
 * Note: Standard WAX accounts only use digits 1-5, but this broader
 * pattern was used in signup validation for compatibility.
 *
 * Used in: src/app/api/auth/signup/route.ts
 */
export const WAX_ACCOUNT_LEGACY_REGEX = /^[a-z0-9.]{1,12}$/;

/**
 * WAX account validation mode options.
 */
export type WaxValidationMode = 'strict' | 'flexible' | 'legacy';

/**
 * Validates a WAX blockchain account name.
 *
 * @param account - The account name to validate
 * @param mode - Validation mode:
 *   - 'strict': Exactly 12 chars, a-z and 1-5 only (standard accounts)
 *   - 'flexible': 1-12 chars with dots (premium/bid names)
 *   - 'legacy': 1-12 chars, allows 0-9 (broader compatibility)
 * @returns true if the account name is valid for the given mode
 *
 * @example
 * isValidWaxAccount('myaccount123', 'strict')   // true (12 chars, a-z1-5)
 * isValidWaxAccount('short.name', 'flexible')   // true (has dot, <12 chars)
 * isValidWaxAccount('account09', 'legacy')      // true (has 0 and 9)
 */
export function isValidWaxAccount(
  account: string,
  mode: WaxValidationMode = 'strict'
): boolean {
  if (!account || typeof account !== 'string') {
    return false;
  }

  switch (mode) {
    case 'strict':
      return WAX_ACCOUNT_STRICT_REGEX.test(account);
    case 'flexible':
      return WAX_ACCOUNT_FLEXIBLE_REGEX.test(account);
    case 'legacy':
      return WAX_ACCOUNT_LEGACY_REGEX.test(account);
    default:
      return WAX_ACCOUNT_STRICT_REGEX.test(account);
  }
}

// =============================================================================
// Required Fields Validation
// =============================================================================

/**
 * Result of required field validation.
 */
export interface ValidationResult {
  /** Whether all required fields are present and non-empty */
  valid: boolean;
  /** List of field names that are missing or empty */
  missing: string[];
}

/**
 * Validates that all required fields are present and non-empty.
 *
 * A field is considered missing if it is:
 * - undefined
 * - null
 * - an empty string ''
 *
 * @param data - The object containing fields to validate
 * @param fields - Array of field names that are required
 * @returns ValidationResult with valid flag and list of missing fields
 *
 * @example
 * const result = validateRequiredFields(
 *   { name: 'John', email: '' },
 *   ['name', 'email', 'phone']
 * );
 * // { valid: false, missing: ['email', 'phone'] }
 */
export function validateRequiredFields<T extends Record<string, unknown>>(
  data: T,
  fields: (keyof T)[]
): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      missing: fields as string[],
    };
  }

  const missing = fields.filter((field) => {
    const value = data[field];
    return value === undefined || value === null || value === '';
  });

  return {
    valid: missing.length === 0,
    missing: missing as string[],
  };
}

/**
 * Formats a list of missing fields into a user-friendly error message.
 *
 * @param missing - Array of missing field names
 * @returns Formatted error message
 *
 * @example
 * formatMissingFieldsError(['email', 'password'])
 * // "Missing required fields: email, password"
 */
export function formatMissingFieldsError(missing: string[]): string {
  if (missing.length === 0) {
    return '';
  }
  if (missing.length === 1) {
    return `Missing required field: ${missing[0]}`;
  }
  return `Missing required fields: ${missing.join(', ')}`;
}

// =============================================================================
// Slug Validation
// =============================================================================

/**
 * Regular expression for validating URL slugs.
 * Allows lowercase letters, numbers, and hyphens.
 *
 * Used for organization slugs and campaign slugs.
 */
export const SLUG_REGEX = /^[a-z0-9-]+$/;

/**
 * Validates a URL slug format.
 *
 * @param slug - The slug to validate
 * @returns true if the slug format is valid
 *
 * @example
 * isValidSlug('my-organization')  // true
 * isValidSlug('My_Organization')  // false (uppercase and underscore)
 */
export function isValidSlug(slug: string): boolean {
  if (!slug || typeof slug !== 'string') {
    return false;
  }
  return SLUG_REGEX.test(slug);
}

// =============================================================================
// Hex Color Validation
// =============================================================================

/**
 * Regular expression for validating hex color codes.
 * Matches both 3-character (#RGB) and 6-character (#RRGGBB) formats.
 */
export const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

/**
 * Validates a hex color code.
 *
 * @param color - The color string to validate (must include # prefix)
 * @returns true if the color is a valid hex code
 *
 * @example
 * isValidHexColor('#FF5733')  // true
 * isValidHexColor('#F53')     // true (shorthand)
 * isValidHexColor('FF5733')   // false (missing #)
 * isValidHexColor('#GGG')     // false (invalid hex)
 */
export function isValidHexColor(color: string): boolean {
  if (!color || typeof color !== 'string') {
    return false;
  }
  return HEX_COLOR_REGEX.test(color);
}

// =============================================================================
// URL Validation
// =============================================================================

/**
 * Validates a URL string.
 *
 * Uses the native URL constructor for robust validation.
 *
 * @param url - The URL string to validate
 * @returns true if the URL is valid
 *
 * @example
 * isValidUrl('https://example.com')          // true
 * isValidUrl('http://localhost:3000/path')   // true
 * isValidUrl('not-a-url')                    // false
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// =============================================================================
// Password Validation
// =============================================================================

/**
 * Minimum required password length.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Validates password meets minimum requirements.
 *
 * @param password - The password to validate
 * @param minLength - Minimum length (default: 8)
 * @returns true if the password meets requirements
 *
 * @example
 * isValidPassword('password123')  // true
 * isValidPassword('short')        // false (too short)
 */
export function isValidPassword(
  password: string,
  minLength: number = MIN_PASSWORD_LENGTH
): boolean {
  if (!password || typeof password !== 'string') {
    return false;
  }
  return password.length >= minLength;
}

/**
 * Password validation result with specific requirements checked.
 */
export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Default password policy options.
 * OWASP: A07:2021 - Identification and Authentication Failures
 */
export const DEFAULT_PASSWORD_POLICY = {
  minLength: MIN_PASSWORD_LENGTH,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: false,
} as const;

/**
 * Validates password with detailed requirements.
 *
 * @param password - The password to validate
 * @param options - Validation options (defaults to OWASP-compliant policy)
 * @returns Validation result with specific errors
 *
 * @example
 * validatePassword('weakpassword')
 * // { valid: false, errors: ['Password must contain at least one uppercase letter'] }
 *
 * @example
 * validatePassword('StrongPass1')
 * // { valid: true, errors: [] }
 */
export function validatePassword(
  password: string,
  options: {
    minLength?: number;
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireNumber?: boolean;
    requireSpecialChar?: boolean;
  } = {}
): PasswordValidationResult {
  const {
    minLength = DEFAULT_PASSWORD_POLICY.minLength,
    requireUppercase = DEFAULT_PASSWORD_POLICY.requireUppercase,
    requireLowercase = DEFAULT_PASSWORD_POLICY.requireLowercase,
    requireNumber = DEFAULT_PASSWORD_POLICY.requireNumber,
    requireSpecialChar = DEFAULT_PASSWORD_POLICY.requireSpecialChar,
  } = options;

  const errors: string[] = [];

  if (!password || typeof password !== 'string') {
    return { valid: false, errors: ['Password is required'] };
  }

  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters long`);
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (requireNumber && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (requireSpecialChar && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// =============================================================================
// Crypto Familiarity Validation
// =============================================================================

/**
 * Valid crypto familiarity values for signup.
 */
export const CRYPTO_FAMILIARITY_VALUES = ['beginner', 'existing-wax'] as const;

export type CryptoFamiliarity = typeof CRYPTO_FAMILIARITY_VALUES[number];

/**
 * Validates crypto familiarity selection.
 *
 * @param value - The value to validate
 * @returns true if the value is a valid crypto familiarity option
 */
export function isValidCryptoFamiliarity(value: string): value is CryptoFamiliarity {
  return CRYPTO_FAMILIARITY_VALUES.includes(value as CryptoFamiliarity);
}

// =============================================================================
// API Validation Helpers
// =============================================================================

/**
 * Result of a field validation that can be used in API routes.
 * Provides a standardized way to validate and return errors.
 */
export type FieldValidationResult =
  | { valid: true }
  | { valid: false; error: string; status: number };

/**
 * Validates a hex color field and returns an API-friendly result.
 *
 * @param color - The color value to validate
 * @param fieldName - Human-readable field name for error messages
 * @returns Validation result with error message if invalid
 *
 * @example
 * ```typescript
 * const result = validateHexColorField(body.primaryColor, "primary color");
 * if (!result.valid) {
 *   return NextResponse.json({ error: result.error }, { status: result.status });
 * }
 * ```
 */
export function validateHexColorField(
  color: string | undefined | null,
  fieldName: string
): FieldValidationResult {
  if (color !== undefined && color !== null && color !== '' && !isValidHexColor(color)) {
    return {
      valid: false,
      error: `Invalid ${fieldName} format. Use hex format (e.g., #3B82F6)`,
      status: 400,
    };
  }
  return { valid: true };
}

/**
 * Validates a URL field and returns an API-friendly result.
 *
 * @param url - The URL value to validate
 * @param fieldName - Human-readable field name for error messages
 * @returns Validation result with error message if invalid
 *
 * @example
 * ```typescript
 * const result = validateUrlField(body.logoUrl, "logo URL");
 * if (!result.valid) {
 *   return NextResponse.json({ error: result.error }, { status: result.status });
 * }
 * ```
 */
export function validateUrlField(
  url: string | undefined | null,
  fieldName: string
): FieldValidationResult {
  if (url !== undefined && url !== null && url !== '' && !isValidUrl(url)) {
    return {
      valid: false,
      error: `Invalid ${fieldName} format`,
      status: 400,
    };
  }
  return { valid: true };
}

/**
 * Validates an email field and returns an API-friendly result.
 *
 * @param email - The email value to validate
 * @param fieldName - Human-readable field name for error messages
 * @param required - Whether the field is required
 * @returns Validation result with error message if invalid
 */
export function validateEmailField(
  email: string | undefined | null,
  fieldName: string = 'email',
  required: boolean = false
): FieldValidationResult {
  if (required && (!email || email.trim() === '')) {
    return {
      valid: false,
      error: `${fieldName} is required`,
      status: 400,
    };
  }
  if (email && email.trim() !== '' && !isValidEmail(email)) {
    return {
      valid: false,
      error: `Invalid ${fieldName} format`,
      status: 400,
    };
  }
  return { valid: true };
}

/**
 * Validates a WAX account field and returns an API-friendly result.
 *
 * @param account - The WAX account to validate
 * @param fieldName - Human-readable field name for error messages
 * @param mode - Validation mode (strict, flexible, or legacy)
 * @param required - Whether the field is required
 * @returns Validation result with error message if invalid
 */
export function validateWaxAccountField(
  account: string | undefined | null,
  fieldName: string = 'WAX account',
  mode: WaxValidationMode = 'strict',
  required: boolean = false
): FieldValidationResult {
  if (required && (!account || account.trim() === '')) {
    return {
      valid: false,
      error: `${fieldName} is required`,
      status: 400,
    };
  }
  if (account && account.trim() !== '' && !isValidWaxAccount(account, mode)) {
    const modeHint = mode === 'strict'
      ? ' (12 characters, lowercase a-z and 1-5)'
      : ' (1-12 characters, lowercase alphanumeric)';
    return {
      valid: false,
      error: `Invalid ${fieldName} format${modeHint}`,
      status: 400,
    };
  }
  return { valid: true };
}

/**
 * Validates multiple hex color fields at once.
 *
 * @param colors - Object mapping field names to color values
 * @returns First validation error found, or valid result
 *
 * @example
 * ```typescript
 * const result = validateHexColorFields({
 *   "primary color": body.primaryColor,
 *   "secondary color": body.secondaryColor,
 *   "background color": body.backgroundColor,
 * });
 * if (!result.valid) {
 *   return NextResponse.json({ error: result.error }, { status: result.status });
 * }
 * ```
 */
export function validateHexColorFields(
  colors: Record<string, string | undefined | null>
): FieldValidationResult {
  for (const [fieldName, color] of Object.entries(colors)) {
    const result = validateHexColorField(color, fieldName);
    if (!result.valid) {
      return result;
    }
  }
  return { valid: true };
}

/**
 * Validates multiple URL fields at once.
 *
 * @param urls - Object mapping field names to URL values
 * @returns First validation error found, or valid result
 */
export function validateUrlFields(
  urls: Record<string, string | undefined | null>
): FieldValidationResult {
  for (const [fieldName, url] of Object.entries(urls)) {
    const result = validateUrlField(url, fieldName);
    if (!result.valid) {
      return result;
    }
  }
  return { valid: true };
}
